import { useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  useConvertTrial,
  useDeclineTrial,
  type TrialStatusResponse,
} from "@/api/hooks/use-billing-trial";
import { formatTimestamp, formatUsd } from "@/lib/format";

interface TrialBannerProps {
  trial: TrialStatusResponse;
  tenantId: string;
}

type DialogMode = "convert" | "decline" | null;

// Coverage % can look alarming on tiny denominators ("50% (1 of 2)") so we
// only show the percentage once the customer has accumulated a reasonable
// sample; below this threshold we show raw counts only.
const COVERAGE_PCT_MIN_REQUESTS = 10;

export function TrialBanner({ trial, tenantId }: TrialBannerProps) {
  const [dialog, setDialog] = useState<DialogMode>(null);
  const convertM = useConvertTrial(tenantId);
  const declineM = useDeclineTrial(tenantId);

  const totalDays = trialTotalDays(trial);
  const dayOfTrial = Math.min(
    totalDays,
    Math.max(1, totalDays - trial.days_remaining + 1),
  );
  const endsAtDisplay = formatTimestamp(trial.trial_ends_at);
  const graceDeadlineDisplay = formatTimestamp(trial.grace_deadline_at);

  // Grace window: trial has technically ended (days_remaining=0) but the
  // post-trial grace_period_hours window is still open, so the customer
  // can still decline. Both flags come from the server so this is a pure
  // function of the response — no client-side clock involved.
  const inGraceWindow = trial.days_remaining === 0 && trial.can_decline;

  const projectedSavings = formatUsd(trial.projected_savings_usd);
  const projectedSavingsPct = Math.round(trial.projected_savings_pct * 100);
  // All requests with a savings row (real shadow OR deterministic estimate) —
  // this is the basis of the projected-savings hero, so any row means we have
  // a number to show.
  const shadowed = trial.shadowed_request_count;
  // Of those, how many fired a REAL shadow call. This is what the coverage line
  // reports, so the displayed % tracks the tenant's sample rate (~50% at 0.5,
  // a little under once cap/error/passthrough-estimate drops are excluded).
  const realShadowed = trial.real_shadow_request_count;
  const total = trial.trial_request_count;
  // Coverage is measured against requests that actually went upstream, not the
  // raw total — a customer's own upstream errors and blocked requests can't be
  // shadowed, so they're surfaced separately rather than tanking the ratio.
  const successful = trial.successful_request_count;
  const excluded = trial.errored_request_count + trial.blocked_request_count;
  const realCoveragePct = Math.round(trial.real_shadow_coverage_pct * 100);
  const heroIsCalculating = shadowed === 0;
  const showCoveragePct = successful >= COVERAGE_PCT_MIN_REQUESTS;

  const closeDialog = () => setDialog(null);

  const handleConvert = () => {
    convertM.mutate(undefined, { onSettled: closeDialog });
  };

  const handleDecline = () => {
    declineM.mutate(undefined, { onSettled: closeDialog });
  };

  return (
    <>
      <div
        data-testid="trial-banner"
        className="rounded-lg border border-electric-blue/40 bg-electric-blue/10 p-5"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-electric-blue">
              {inGraceWindow ? "Trial ended" : "Trial period"}
            </p>
            {inGraceWindow && !trial.card_on_file ? (
              <p className="text-sm text-fg">
                Your trial ended on{" "}
                <span className="font-mono">{endsAtDisplay}</span> with no card
                on file. Your requests are now forwarded to your provider
                unchanged, with nothing optimized or billed, until a card is
                added.
              </p>
            ) : inGraceWindow ? (
              <p className="text-sm text-fg">
                Your trial ended on{" "}
                <span className="font-mono">{endsAtDisplay}</span>. You have
                until{" "}
                <span className="font-mono">{graceDeadlineDisplay}</span> to
                decline before auto-conversion.
              </p>
            ) : (
              <p className="text-sm text-fg">
                Day <span className="font-mono font-medium">{dayOfTrial}</span>{" "}
                of <span className="font-mono font-medium">{totalDays}</span> ·
                Auto-converts on{" "}
                <span className="font-mono">{endsAtDisplay}</span>.
              </p>
            )}
            <p className="text-xs text-mid-gray">
              Your real requests are running unmodified upstream. Phantm is
              analyzing them in the background to project what we'd save you.
            </p>
            <div className="pt-1">
              {heroIsCalculating ? (
                <p
                  data-testid="trial-projected-savings"
                  className="text-2xl font-semibold text-off-white"
                >
                  Calculating…
                </p>
              ) : (
                <p
                  data-testid="trial-projected-savings"
                  className="text-2xl font-semibold text-off-white font-mono"
                >
                  {projectedSavings} ({projectedSavingsPct}%)
                </p>
              )}
              <p className="text-xs text-mid-gray">
                projected savings this week
              </p>
            </div>
            <p
              data-testid="trial-coverage-line"
              className="text-xs text-mid-gray"
            >
              {total === 0 ? (
                "Waiting for your first request"
              ) : successful === 0 ? (
                <>
                  No successful requests to analyze yet
                  {excluded > 0 ? (
                    <>
                      {" "}· <span className="font-mono">{excluded}</span>{" "}
                      errored or blocked upstream
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <span className="font-mono">{realShadowed}</span> of{" "}
                  <span className="font-mono">{successful}</span> successful
                  request{successful === 1 ? "" : "s"} shadowed
                  {showCoveragePct ? (
                    <>
                      {" "}(<span className="font-mono">{realCoveragePct}%</span>)
                    </>
                  ) : null}
                  {excluded > 0 ? (
                    <>
                      {" "}· <span className="font-mono">{excluded}</span>{" "}
                      excluded (errored or blocked upstream)
                    </>
                  ) : null}
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-start gap-2">
            {trial.card_on_file ? (
              <button
                type="button"
                onClick={() => setDialog("convert")}
                disabled={!trial.can_convert}
                className="rounded-md bg-electric-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-electric-blue/80 disabled:opacity-50 transition-colors"
              >
                Convert to paid
              </button>
            ) : (
              <div className="space-y-1">
                <a
                  href="/billing"
                  className="inline-block rounded-md bg-electric-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-electric-blue/80 transition-colors"
                >
                  Add a card
                </a>
                <p className="text-[11px] text-mid-gray">Converting needs a card on file.</p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setDialog("decline")}
              disabled={!trial.can_decline}
              className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-2 disabled:opacity-50 transition-colors"
            >
              Decline trial
            </button>
          </div>
        </div>
        {trial.cap_reached && (
          <div
            data-testid="trial-cap-reached-banner"
            className="mt-4 rounded-md border border-amber-400/50 bg-amber-400/10 p-3 text-xs text-amber-200"
          >
            Projected savings frozen — Phantm's analysis budget has been
            reached. The amount above is your final trial-week number.
            Contact support to extend the analysis window.
          </div>
        )}
      </div>

      {dialog === "convert" && (
        <ConfirmDialog
          title="Activate Phantm?"
          message="Starting now, your requests will be routed through Phantm's optimization. You will not be charged anything for the trial week regardless of conversion."
          confirmLabel="Activate"
          variant="primary"
          isPending={convertM.isPending}
          onConfirm={handleConvert}
          onCancel={closeDialog}
        />
      )}

      {dialog === "decline" && (
        <ConfirmDialog
          title="Decline trial?"
          message="Your requests will keep working: they will be forwarded to your provider unchanged, with nothing optimized and nothing billed. You will not be charged anything for the trial week. To turn optimization on later, contact Phantm."
          confirmLabel="Decline trial"
          variant="danger"
          isPending={declineM.isPending}
          onConfirm={handleDecline}
          onCancel={closeDialog}
        />
      )}
    </>
  );
}

function trialTotalDays(trial: TrialStatusResponse): number {
  if (!trial.trial_started_at || !trial.trial_ends_at) return 7;
  const start = new Date(trial.trial_started_at).getTime();
  const end = new Date(trial.trial_ends_at).getTime();
  if (isNaN(start) || isNaN(end) || end <= start) return 7;
  return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
}
